import React, { Component } from "react";
import PropTypes from "prop-types";

class Quiz extends Component {
  static propTypes = {
    questions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        prompt: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(PropTypes.string).isRequired,
        answerIndex: PropTypes.number.isRequired, // 0-based
        // optional extras:
        correctAnswer: PropTypes.string,          // if provided, used for the reveal text
        explanation: PropTypes.string,            // optional explanation text
      })
    ).isRequired,
    onComplete: PropTypes.func.isRequired,
    requireAnswer: PropTypes.bool,
  };

  static defaultProps = {
    requireAnswer: true,
  };

  constructor(props) {
    super(props);
    this.state = {
      step: 1,                 // 1-based
      selectedIndex: null,     // 0-based or null
      answersGiven: {},        // { step: index|null }
    };
  }

  handleSelect = (idx) => {
    // If an answer is already chosen for this question, ignore further clicks
    if (this.state.selectedIndex !== null) return;

    this.setState((prev) => ({
      selectedIndex: idx,
      answersGiven: { ...prev.answersGiven, [prev.step]: idx },
    }));
  };

  handleNext = () => {
    const { step, selectedIndex } = this.state;
    const total = this.props.questions.length;

    if (selectedIndex === null && !this.props.requireAnswer) {
      this.setState(
        (prev) => ({
          answersGiven: { ...prev.answersGiven, [prev.step]: null },
        }),
        () => this.advanceOrComplete(step, total)
      );
      return;
    }

    this.advanceOrComplete(step, total);
  };

  advanceOrComplete(step, total) {
    if (step >= total) {
      this.props.onComplete({ total, answers: this.state.answersGiven });
      this.setState({ step: step + 1, selectedIndex: null });
    } else {
      this.setState({ step: step + 1, selectedIndex: null });
    }
  }

  render() {
    const { questions } = this.props;
    const { step, selectedIndex } = this.state;

    const total = questions.length;
    if (total === 0) {
      return (
        <section className="quiz section">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="large-12 cell">
                <h2>No questions available.</h2>
              </div>
            </div>
          </div>
        </section>
      );
    }

    const inProgress = step <= total;
    const current = inProgress ? questions[step - 1] : null;
    const reveal = selectedIndex !== null;

    // Determine the text we show under the answers when revealed
    const revealCorrectText =
      current?.correctAnswer ??
      (current ? current.options[current.answerIndex] : "");

    return (
      <section className="quiz section" aria-live="polite" style={{ backgroundImage: "url(https://s3.us-east-1.amazonaws.com/cdn.fanduel.com/mlb-sweepstakes/images/quiz-bg.jpg)" }}>
        <div className="grid-container">
          <div className="grid-x grid-padding-x">
            <div className="small-12 small-offset-0 medium-10 medium-offset-1 large-8 large-offset-2 cell">
              <h3 className="quiz__h3">Can You Call the Game?</h3>
              <p className="quiz__p">Take the Responsible Gaming Quiz and test your smarts.</p>
            </div>

            {inProgress ? (
              <div className="small-12 small-offset-0 medium-10 medium-offset-1 large-8 large-offset-2 cell">
                <h2 className="quiz__question">
                  {step}. {current.prompt}
                </h2>

                <div className="quiz__answers" style={{ pointerEvents: "auto" }}>
                  {current.options.map((opt, idx) => {
                    const isSelected = selectedIndex === idx;
                    const isCorrect = reveal && idx === current.answerIndex;

                    return (
                      <button
                        key={idx}
                        type="button"
                        className={
                          "quiz__answer" +
                          (isSelected ? " is-selected" : "") +
                          (isCorrect ? " is-correct" : "")
                        }
                        onClick={() => this.handleSelect(idx)}
                        // NOTE: we do NOT disable buttons; we ignore later clicks in handleSelect
                        aria-pressed={isSelected}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>

                {reveal && (
                  <div className="quiz__correct-explainer" role="status" aria-live="assertive">
                    <h4 className="quiz__h4">Correct answer: {revealCorrectText}</h4>
                    {current.explanation && (
                      <p className="quiz__p">Explanation: {current.explanation}</p>
                    )}
                  </div>
                )}

                <div className="quiz__controls">
                  <button
                    className="quiz__next-btn"
                    type="button"
                    disabled={this.props.requireAnswer ? selectedIndex === null : false}
                    onClick={this.handleNext}
                  >
                    Next &#9656;
                  </button>
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </section>
    );
  }
}

export default Quiz;
